// Sección HISTORIAL SMS.
//
// Muestra TODO intento de envío (campaña, automatización o manual) con su
// estado y, sobre todo, con el error del proveedor tal cual lo devolvió el
// backend. Esa franqueza es deliberada: un "Error de envío" genérico no deja
// depurar nada, mientras que un "PROVIDER_DISABLED: SMS.to is not enabled or
// has no API key configured" dice exactamente qué tocar.
//
// Fuentes de datos (backend/src/routes/sms.js):
//   GET    /sms/history         → { history[], total, page, limit, statuses, origins }
//   GET    /sms/history/stats   → { total, sent, delivered, failed, pending,
//                                   segments, cost, currency, deliveryRate }
//   GET    /sms/history/:id     → una fila completa
//   GET    /sms/provider        → { config|null, providers[], ready, error }
//   PUT    /sms/provider        → upsert (omitir apiKey/apiSecret los conserva)
//   DELETE /sms/provider        → borra la configuración del tenant
//
// OJO con las credenciales: el backend NUNCA devuelve la clave real, sólo
// `apiKeyMasked` y un booleano `hasApiSecret`. Aquí no se reconstruye ningún
// secreto, no se guarda nada en localStorage y no se imprime en consola.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { G, fmt, curSym, getAppTz, fmtDay, fmtDayTime } from '../theme';
import { smsApi } from '../api';

// ── Diccionarios de presentación ───────────────────────────────────────────
// Las claves son los valores EXACTOS que persiste el backend
// (HISTORY_STATUSES / HISTORY_ORIGINS en routes/sms.js).

const STATUS_META = {
  pending:   { label: 'Pendiente', badge: 'badge-gray',   color: G.muted },
  sent:      { label: 'Enviado',   badge: 'badge-blue',   color: G.blue },
  delivered: { label: 'Entregado', badge: 'badge-green',  color: G.green },
  failed:    { label: 'Fallido',   badge: 'badge-red',    color: G.red },
};

const ORIGIN_META = {
  campaign:   { label: 'Campaña',        badge: 'badge-gold' },
  automation: { label: 'Automatización', badge: 'badge-blue' },
  manual:     { label: 'Manual',         badge: 'badge-gray' },
};

const STATUS_PILLS = [
  { v: '', l: 'Todos' },
  { v: 'delivered', l: 'Entregado' },
  { v: 'sent', l: 'Enviado' },
  { v: 'pending', l: 'Pendiente' },
  { v: 'failed', l: 'Fallido' },
];

const ORIGIN_PILLS = [
  { v: '', l: 'Todos' },
  { v: 'campaign', l: 'Campaña' },
  { v: 'automation', l: 'Automatización' },
  { v: 'manual', l: 'Manual' },
];

// `off: true` = columna disponible pero oculta por defecto.
const SMS_COLS = [
  { key: 'createdAt',         label: 'Fecha',        sortable: true },
  { key: 'to',                label: 'Destinatario', sortable: true, mono: true },
  { key: 'origin',            label: 'Origen',       sortable: true },
  { key: 'campaignName',      label: 'Campaña',      sortable: false, off: true },
  { key: 'content',           label: 'Contenido',    sortable: false },
  { key: 'encoding',          label: 'Codif.',       sortable: true, off: true },
  { key: 'segments',          label: 'Segmentos',    sortable: true, num: true },
  { key: 'cost',              label: 'Coste',        sortable: true, num: true },
  { key: 'status',            label: 'Estado',       sortable: true },
  { key: 'error',             label: 'Error',        sortable: false },
  { key: 'providerMessageId', label: 'ID proveedor', sortable: false, off: true, mono: true },
];

const DEFAULT_COLS = SMS_COLS.filter((c) => !c.off).map((c) => c.key);

// Proveedores soportados por el backend. Se usa como respaldo si /provider no
// devuelve la lista (API caída): así el formulario sigue siendo usable.
const FALLBACK_PROVIDERS = [
  { id: 'twilio', label: 'Twilio', needsSecret: true },
  { id: 'smsto', label: 'SMS.to', needsSecret: false },
];

const EXPORT_PAGE = 200; // tope de `limit` que acepta el backend

// ── Helpers ────────────────────────────────────────────────────────────────

// Mensaje de error REAL de la API cuando lo hay; si no, el de respaldo.
function apiErr(e, fallback) {
  const m = e && e.response && e.response.data && e.response.data.error;
  return m ? String(m) : fallback;
}

// fmt() es el formateador canónico del panel (EUR, con conversión opcional a
// USD del operador). Si el proveedor factura en otra divisa se muestra tal
// cual, SIN convertir: inventarse un tipo de cambio sería un número falso.
function money(value, currency, decimals = 4) {
  const n = Number(value);
  if (!isFinite(n)) return '--';
  const cur = String(currency || 'EUR').toUpperCase();
  if (cur === 'EUR') return fmt(n, { decimals });
  return `${curSym(cur)}${n.toFixed(decimals)}`;
}

function statusMeta(s) {
  return STATUS_META[String(s || 'pending')] || { label: s || '--', badge: 'badge-gray', color: G.dim };
}

function originMeta(o) {
  return ORIGIN_META[String(o || 'manual')] || { label: o || '--', badge: 'badge-gray' };
}

function pct(part, whole) {
  const p = Number(part), w = Number(whole);
  if (!isFinite(p) || !isFinite(w) || w <= 0) return null;
  return Math.round((p / w) * 1000) / 10;
}

function num(n) {
  const v = Number(n);
  return isFinite(v) ? v.toLocaleString('es-ES') : '--';
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal de detalle de un envío
// ═══════════════════════════════════════════════════════════════════════════

function Field({ label, children, mono }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: G.white, fontFamily: mono ? 'DM Mono, monospace' : undefined, wordBreak: 'break-word' }}>
        {children}
      </div>
    </div>
  );
}

function DetailModal({ row, onClose }) {
  const st = statusMeta(row.status);
  const or = originMeta(row.origin);
  const dt = fmtDayTime(row.createdAt || row.date);
  const sent = row.sentAt ? fmtDayTime(row.sentAt) : null;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 14, width: 'min(680px, 100%)', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 22px', borderBottom: `1px solid ${G.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 21, color: G.white }}>Detalle del SMS</div>
            <span className={`badge ${st.badge}`}>{st.label}</span>
            <span className={`badge ${or.badge}`}>{or.label}</span>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cerrar</button>
        </div>

        <div style={{ padding: 22, display: 'grid', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
            <Field label="Destinatario" mono>{row.to || '--'}</Field>
            <Field label="Fecha" mono>{dt.day}{dt.time ? ` · ${dt.time}` : ''}</Field>
            <Field label="Enviado" mono>{sent ? `${sent.day} · ${sent.time}` : '--'}</Field>
            <Field label="Campaña">{row.campaignName || '--'}</Field>
            <Field label="Segmentos" mono>{row.segments != null ? row.segments : '--'}</Field>
            <Field label="Caracteres" mono>{row.chars != null ? row.chars : '--'}</Field>
            <Field label="Codificación" mono>{row.encoding || '--'}</Field>
            <Field label="Coste" mono>{money(row.cost, row.currency)}</Field>
            <Field label="Proveedor">{row.provider || '--'}</Field>
            <Field label="ID proveedor" mono>{row.providerMessageId || '--'}</Field>
          </div>

          <div>
            <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Contenido</div>
            <div style={{ background: G.black, border: `1px solid ${G.border}`, borderRadius: 10, padding: 14, fontSize: 13, color: G.white, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.55 }}>
              {row.content || row.body || <span style={{ color: G.muted }}>(vacío)</span>}
            </div>
          </div>

          {row.error && (
            <div>
              <div style={{ fontSize: 10, color: G.red, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                Error del proveedor (literal)
              </div>
              {/* Sin maquillar: es el texto que permite saber qué falló. */}
              <div style={{ background: G.redDim, border: `1px solid ${G.red}44`, borderRadius: 10, padding: 14, fontSize: 12.5, color: G.red, fontFamily: 'DM Mono, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                {row.error}
              </div>
            </div>
          )}

          <div style={{ fontSize: 10.5, color: G.muted, fontFamily: 'DM Mono, monospace', borderTop: `1px solid ${G.border}`, paddingTop: 12 }}>
            id: {row.id || '--'} · horas en {getAppTz()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Modal de configuración del proveedor SMS
// ═══════════════════════════════════════════════════════════════════════════

function ProviderModal({ data, onClose, onSaved, toast }) {
  const cfg = (data && data.config) || null;
  const providers = (data && Array.isArray(data.providers) && data.providers.length)
    ? data.providers
    : FALLBACK_PROVIDERS;

  const [form, setForm] = useState({
    provider: (cfg && cfg.provider) || providers[0].id,
    senderId: (cfg && cfg.senderId) || '',
    enabled: cfg ? cfg.enabled !== false : true,
    pricePerSegment: cfg && cfg.pricePerSegment != null ? String(cfg.pricePerSegment) : '',
    currency: (cfg && cfg.currency) || 'EUR',
    throttleMs: cfg && cfg.throttleMs != null ? String(cfg.throttleMs) : '120',
  });
  // Los secretos SIEMPRE empiezan vacíos. Vacío = "no tocar el guardado".
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  // SMS de prueba: usa la configuración YA GUARDADA (el backend lee el proveedor
  // de la base de datos), por eso sólo se ofrece cuando hay proveedor guardado.
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selected = providers.find((p) => p.id === form.provider) || providers[0];
  const needsSecret = !!(selected && selected.needsSecret);
  // La API key guardada sólo cuenta si NO se cambia de proveedor: cambiarlo
  // deja unas credenciales que ya no sirven.
  const sameProvider = !!(cfg && cfg.provider === form.provider);
  const keyOnFile = !!(cfg && cfg.hasApiKey && sameProvider);
  const secretOnFile = !!(cfg && cfg.hasApiSecret && sameProvider);

  const save = async () => {
    if (!keyOnFile && !apiKey.trim()) {
      toast('La API key es obligatoria la primera vez que configuras el proveedor', 'error');
      return;
    }
    if (!form.senderId.trim()) {
      toast('El remitente (sender ID / número) es obligatorio: sin él los envíos fallan', 'error');
      return;
    }
    const price = form.pricePerSegment === '' ? 0 : parseFloat(form.pricePerSegment);
    if (!isFinite(price) || price < 0) {
      toast('El precio por segmento no es válido', 'error');
      return;
    }
    const throttle = form.throttleMs === '' ? 120 : parseInt(form.throttleMs, 10);
    if (!isFinite(throttle) || throttle < 0 || throttle > 10000) {
      toast('La pausa entre envíos debe estar entre 0 y 10000 ms', 'error');
      return;
    }

    const payload = {
      provider: form.provider,
      senderId: form.senderId.trim(),
      enabled: !!form.enabled,
      pricePerSegment: price,
      currency: form.currency,
      throttleMs: throttle,
    };
    // Sólo se mandan los secretos que el operador ha escrito; si los deja en
    // blanco, el backend conserva los que ya tenía (no los pisa con "").
    if (apiKey.trim()) payload.apiKey = apiKey.trim();
    if (apiSecret.trim()) payload.apiSecret = apiSecret.trim();

    setSaving(true);
    try {
      const res = await smsApi.saveProvider(payload);
      setApiKey('');
      setApiSecret('');
      if (res && res.error) toast(res.error, 'error');
      else toast('Proveedor SMS guardado', 'success');
      onSaved();
      onClose();
    } catch (e) {
      toast(apiErr(e, 'No se pudo guardar el proveedor SMS'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const to = testTo.trim();
    if (!to) { toast('Escribe el móvil (con prefijo, p. ej. +44…) al que enviar la prueba', 'error'); return; }
    if (!cfg) { toast('Guarda primero el proveedor: la prueba usa la configuración guardada', 'error'); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await smsApi.testProvider(to);
      setTestResult({ ok: true, msg: `SMS de prueba enviado a ${to}. Compruébalo en el móvil y en el historial.` });
      toast('SMS de prueba enviado', 'success');
      onSaved();
    } catch (e) {
      const data = e && e.response && e.response.data;
      const msg = (data && (data.error || data.message)) || apiErr(e, 'No se pudo enviar el SMS de prueba');
      setTestResult({ ok: false, msg: String(msg) });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await smsApi.deleteProvider();
      toast('Configuración del proveedor eliminada', 'success');
      onSaved();
      onClose();
    } catch (e) {
      toast(apiErr(e, 'No se pudo eliminar la configuración'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={() => !saving && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: G.card, border: `1px solid ${G.border}`, borderRadius: 14, width: 'min(620px, 100%)', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 22px', borderBottom: `1px solid ${G.border}` }}>
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 21, color: G.white }}>Proveedor de SMS</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cerrar</button>
        </div>

        <div style={{ padding: 22 }}>
          <div style={{ fontSize: 12, color: G.dim, marginBottom: 18, lineHeight: 1.6 }}>
            Las credenciales se guardan cifradas y el panel nunca las vuelve a mostrar.
            Deja los campos de clave en blanco para conservar las que ya hay guardadas.
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Proveedor</label>
              <select className="form-input" value={form.provider} onChange={(e) => set('provider', e.target.value)}>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Remitente (sender ID / número)</label>
              <input
                className="form-input"
                value={form.senderId}
                onChange={(e) => set('senderId', e.target.value)}
                placeholder={form.provider === 'twilio' ? '+34600111222' : 'MITIENDA'}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              API key {form.provider === 'twilio' ? '(Account SID)' : '(API key)'}
            </label>
            <input
              className="form-input"
              type="password"
              autoComplete="new-password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={keyOnFile ? 'Guardada — escribe sólo si quieres cambiarla' : 'Pega aquí la clave'}
            />
            <div style={{ fontSize: 11, color: keyOnFile ? G.muted : G.orange, marginTop: 5, fontFamily: 'DM Mono, monospace' }}>
              {keyOnFile
                ? `Guardada: ${cfg.apiKeyMasked || '••••••••'}`
                : 'Sin clave guardada para este proveedor — es obligatoria.'}
            </div>
          </div>

          {needsSecret && (
            <div className="form-group">
              <label className="form-label">API secret (Auth Token)</label>
              <input
                className="form-input"
                type="password"
                autoComplete="new-password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder={secretOnFile ? 'Guardado — escribe sólo si quieres cambiarlo' : 'Pega aquí el token'}
              />
              <div style={{ fontSize: 11, color: secretOnFile ? G.muted : G.orange, marginTop: 5, fontFamily: 'DM Mono, monospace' }}>
                {secretOnFile
                  ? 'Guardado: ••••••••'
                  : `${selected.label} necesita secret: sin él los envíos fallarán con PROVIDER_DISABLED.`}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Precio por segmento</label>
              <input
                className="form-input"
                type="number"
                min="0"
                step="0.0001"
                value={form.pricePerSegment}
                onChange={(e) => set('pricePerSegment', e.target.value)}
                placeholder="0.0000"
              />
              <div style={{ fontSize: 11, color: G.muted, marginTop: 5 }}>
                Sólo para estimar coste; no lo factura ZeroRiskPay.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Divisa</label>
              <select className="form-input" value={form.currency} onChange={(e) => set('currency', e.target.value)}>
                {['EUR', 'USD', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Pausa entre envíos (ms)</label>
              <input
                className="form-input"
                type="number"
                min="0"
                max="10000"
                step="10"
                value={form.throttleMs}
                onChange={(e) => set('throttleMs', e.target.value)}
              />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: G.white, cursor: 'pointer', padding: '10px 0' }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Envío activado
            <span style={{ fontSize: 11, color: G.muted }}>
              (si lo desactivas, cada intento queda registrado como Fallido)
            </span>
          </label>

          <div style={{ marginTop: 14, borderTop: `1px solid ${G.border}`, paddingTop: 16 }}>
            <label className="form-label">Enviar un SMS de prueba</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                className="form-input"
                style={{ flex: '1 1 200px' }}
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="+44 7911 123456"
                disabled={!cfg || testing}
              />
              <button className="btn btn-ghost btn-sm" onClick={sendTest} disabled={!cfg || testing || saving}>
                {testing ? 'Enviando…' : 'Enviar prueba'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: testResult ? (testResult.ok ? G.green : G.red) : G.muted, marginTop: 6, lineHeight: 1.5 }}>
              {testResult
                ? testResult.msg
                : (cfg
                  ? 'Envía un SMS real con el proveedor guardado (se registra en el historial y consume saldo).'
                  : 'Guarda primero el proveedor; la prueba usa las credenciales guardadas.')}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', marginTop: 18, borderTop: `1px solid ${G.border}`, paddingTop: 18, flexWrap: 'wrap' }}>
            <div>
              {cfg && !confirmDel && (
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDel(true)} disabled={saving}>
                  Eliminar configuración
                </button>
              )}
              {cfg && confirmDel && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: G.red }}>¿Eliminar y dejar de enviar?</span>
                  <button className="btn btn-danger btn-sm" onClick={remove} disabled={saving}>Sí, eliminar</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDel(false)} disabled={saving}>No</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancelar</button>
              <button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar proveedor'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sección principal
// ═══════════════════════════════════════════════════════════════════════════

export default function SmsHistory({ showToast }) {
  const toast = useCallback((msg, kind) => {
    if (typeof showToast === 'function') showToast(msg, kind || 'info');
  }, [showToast]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Filtros. Las pills y las fechas se aplican al instante; el buscador
  // espera a Enter / botón para no lanzar una petición por tecla.
  const [status, setStatus] = useState('');
  const [origin, setOrigin] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  const [visibleCols, setVisibleCols] = useState(() => new Set(DEFAULT_COLS));
  const [colsOpen, setColsOpen] = useState(false);
  const [dense, setDense] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [detail, setDetail] = useState(null);
  const [provider, setProvider] = useState(null);
  const [provLoading, setProvLoading] = useState(true);
  const [provOpen, setProvOpen] = useState(false);

  // ── Carga ────────────────────────────────────────────────────────────────

  const filterParams = useMemo(() => {
    const p = {};
    if (status) p.status = status;
    if (origin) p.origin = origin;
    if (search) p.search = search;
    if (from) p.from = `${from}T00:00:00`;
    // El backend compara createdAt <= to_date, así que hay que estirar el día
    // completo o el propio día elegido quedaría fuera.
    if (to) p.to_date = `${to}T23:59:59.999`;
    return p;
  }, [status, origin, search, from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        smsApi.listHistory({ ...filterParams, page, limit, sort, order }),
        smsApi.historyStats(filterParams).catch(() => null),
      ]);
      setRows(Array.isArray(list && list.history) ? list.history : []);
      setTotal(Number(list && list.total) || 0);
      setStats(st && typeof st === 'object' ? st : null);
      setFailed(false);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setStats(null);
      setFailed(true);
      toast(apiErr(e, 'No se pudo cargar el historial de SMS'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, limit, sort, order, toast]);

  const loadProvider = useCallback(async () => {
    setProvLoading(true);
    try {
      const d = await smsApi.getProvider();
      setProvider(d && typeof d === 'object' ? d : null);
    } catch (e) {
      setProvider(null);
      toast(apiErr(e, 'No se pudo consultar el proveedor de SMS'), 'error');
    } finally {
      setProvLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProvider(); }, [loadProvider]);

  // ── Interacción ──────────────────────────────────────────────────────────

  const pick = (setter) => (v) => { setter(v); setPage(1); };
  const applySearch = () => { setSearch(searchDraft.trim()); setPage(1); };

  const resetFilters = () => {
    setStatus(''); setOrigin(''); setSearch(''); setSearchDraft('');
    setFrom(''); setTo(''); setPage(1);
  };

  const toggleSort = (key, sortable) => {
    if (!sortable) return;
    if (sort === key) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setOrder('desc'); }
    setPage(1);
  };

  const toggleCol = (key) => setVisibleCols((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const openDetail = async (row) => {
    // Se pinta ya con lo que hay en la tabla y se refresca con la fila
    // completa del backend (trae `chars`, `sentAt`, `body`…).
    setDetail(row);
    try {
      const full = await smsApi.getHistory(row.id);
      if (full && full.id) setDetail(full);
    } catch (e) {
      toast(apiErr(e, 'No se pudo cargar el detalle del envío'), 'error');
    }
  };

  // ── Exportación ──────────────────────────────────────────────────────────

  const exportCell = (r, key) => {
    switch (key) {
      case 'createdAt': return fmtDay(r.createdAt || r.date);
      case 'origin': return originMeta(r.origin).label;
      case 'status': return statusMeta(r.status).label;
      case 'content': return r.content || r.body || '';
      case 'cost': return Number(r.cost || 0).toFixed(4);
      case 'segments': return Number(r.segments || 0);
      default: return r[key] == null ? '' : r[key];
    }
  };

  // Exporta TODO lo que casa con los filtros actuales, no sólo la página.
  const exportAll = async (kind) => {
    if (exporting) return;
    setExporting(true);
    try {
      const all = [];
      for (let p = 1; ; p++) {
        const data = await smsApi.listHistory({ ...filterParams, page: p, limit: EXPORT_PAGE, sort, order });
        const batch = Array.isArray(data && data.history) ? data.history : [];
        all.push(...batch);
        if (batch.length < EXPORT_PAGE || all.length >= (Number(data && data.total) || 0)) break;
        if (p > 200) break; // salvavidas contra un total mal calculado
      }
      if (!all.length) {
        toast('No hay envíos que exportar con estos filtros', 'error');
        return;
      }
      const cols = SMS_COLS.filter((c) => visibleCols.has(c.key));
      const header = cols.map((c) => c.label);
      const body = all.map((r) => cols.map((c) => exportCell(r, c.key)));
      const stamp = new Date().toISOString().split('T')[0];

      if (kind === 'csv') {
        const csv = [header, ...body]
          .map((r) => r.map((v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`).join(','))
          .join('\n');
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `sms_historial_${stamp}.csv`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Historial SMS');
        XLSX.writeFile(wb, `sms_historial_${stamp}.xlsx`);
      }
      toast(`${all.length} envíos exportados`, 'success');
    } catch (e) {
      toast(apiErr(e, 'Error al exportar el historial'), 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Derivados ────────────────────────────────────────────────────────────

  const cols = SMS_COLS.filter((c) => visibleCols.has(c.key));
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pad = dense ? '6px 12px' : undefined;
  const textMax = dense ? 220 : 320;

  const cfg = provider && provider.config;
  const provReady = !!(provider && provider.ready);
  const provError = (provider && provider.error) || '';
  const hasFilters = !!(status || origin || search || from || to);

  // Contadores por pill: sólo cuando NO hay filtro de estado, porque /stats
  // responde a los filtros aplicados y si no sería un número engañoso.
  const s = stats || {};
  const sentOnly = Math.max(0, (Number(s.sent) || 0) - (Number(s.delivered) || 0));
  const pillCount = (v) => {
    if (status !== '' || !stats) return null;
    if (v === '') return Number(s.total) || 0;
    if (v === 'delivered') return Number(s.delivered) || 0;
    if (v === 'failed') return Number(s.failed) || 0;
    if (v === 'pending') return Number(s.pending) || 0;
    if (v === 'sent') return sentOnly;
    return null;
  };

  const failRate = pct(s.failed, s.total);
  const deliveryRate = (Number(s.sent) || 0) > 0 ? Number(s.deliveryRate) : null;

  // ── Celdas ───────────────────────────────────────────────────────────────

  const renderCell = (r, key) => {
    switch (key) {
      case 'createdAt': {
        const d = fmtDayTime(r.createdAt || r.date);
        if (dense) return <span>{d.day}{d.time ? ` ${d.time}` : ''}</span>;
        return (
          <span>
            {d.day}
            {d.time && <span style={{ color: G.muted, marginLeft: 6 }}>{d.time}</span>}
          </span>
        );
      }
      case 'to':
        return r.to || <span style={{ color: G.muted }}>--</span>;
      case 'origin': {
        const o = originMeta(r.origin);
        return <span className={`badge ${o.badge}`}>{o.label}</span>;
      }
      case 'campaignName':
        return r.campaignName || <span style={{ color: G.muted }}>--</span>;
      case 'content': {
        const txt = r.content || r.body || '';
        if (!txt) return <span style={{ color: G.muted }}>--</span>;
        return (
          <span
            title={txt}
            style={{ display: 'block', maxWidth: textMax, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: G.dim }}
          >
            {txt}
          </span>
        );
      }
      case 'encoding':
        return <span className="mono" style={{ color: r.encoding === 'UCS2' ? G.orange : G.dim }}>{r.encoding || '--'}</span>;
      case 'segments':
        return r.segments != null ? r.segments : '--';
      case 'cost':
        return money(r.cost, r.currency);
      case 'status': {
        const st = statusMeta(r.status);
        return <span className={`badge ${st.badge}`}>{st.label}</span>;
      }
      case 'error': {
        if (!r.error) return <span style={{ color: G.muted }}>--</span>;
        // Literal, sin traducir ni resumir: es lo que permite depurar.
        return (
          <span
            title={r.error}
            style={{ display: 'block', maxWidth: textMax, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: G.red, fontFamily: 'DM Mono, monospace', fontSize: 11.5 }}
          >
            {r.error}
          </span>
        );
      }
      case 'providerMessageId':
        return r.providerMessageId || <span style={{ color: G.muted }}>--</span>;
      default:
        return r[key] == null || r[key] === '' ? '--' : String(r[key]);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Aviso de proveedor: lo primero que debe ver el operador, porque sin
          proveedor NINGÚN envío sale. */}
      {!provLoading && !provReady && (
        <div
          className="card"
          style={{ borderColor: `${G.red}66`, background: `linear-gradient(180deg, ${G.redDim}, transparent)`, marginBottom: 18 }}
        >
          <div className="card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
              <div style={{ fontSize: 22, lineHeight: 1 }}>⚠</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, color: G.white, fontWeight: 600, marginBottom: 4 }}>
                  {cfg ? 'El proveedor de SMS no está listo' : 'No hay proveedor de SMS configurado'}
                </div>
                <div style={{ fontSize: 12.5, color: G.dim, lineHeight: 1.6 }}>
                  Mientras siga así, <strong style={{ color: G.red }}>todos los envíos fallarán</strong> y
                  quedarán registrados aquí como Fallidos con el motivo exacto.
                </div>
                {provError && (
                  <div style={{ marginTop: 8, fontSize: 11.5, color: G.red, fontFamily: 'DM Mono, monospace', wordBreak: 'break-word' }}>
                    {provError}
                  </div>
                )}
              </div>
            </div>
            <button className="btn btn-gold btn-sm" onClick={() => setProvOpen(true)} style={{ whiteSpace: 'nowrap' }}>
              Configurar proveedor
            </button>
          </div>
        </div>
      )}

      {/* Cabecera de estadísticas */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Total intentos</div>
          <div className="kpi-value">{stats ? num(s.total) : '--'}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {stats ? `${num(s.pending)} pendientes` : 'sin datos'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Entregados</div>
          <div className="kpi-value" style={{ color: stats && Number(s.delivered) > 0 ? G.green : undefined }}>
            {stats ? num(s.delivered) : '--'}
          </div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {stats ? `de ${num(s.sent)} enviados` : 'sin datos'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Fallidos</div>
          <div className="kpi-value" style={{ color: stats && Number(s.failed) > 0 ? G.red : undefined }}>
            {stats ? num(s.failed) : '--'}
          </div>
          <div className="kpi-delta" style={{ color: failRate != null && failRate > 0 ? G.red : G.muted }}>
            {failRate != null ? `${failRate}% del total` : 'sin datos'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Tasa de entrega</div>
          <div className="kpi-value">{deliveryRate != null ? `${deliveryRate}%` : '--'}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {deliveryRate != null ? 'entregados / enviados' : 'sin datos suficientes'}
          </div>
          {deliveryRate != null && (
            <div className="progress" style={{ marginTop: 8 }}>
              <div className="progress-fill" style={{ width: `${Math.min(100, Math.max(0, deliveryRate))}%` }} />
            </div>
          )}
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Coste acumulado</div>
          <div className="kpi-value">
            {stats ? money(s.cost, s.currency, Number(s.cost) >= 1 ? 2 : 4) : '--'}
          </div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {stats ? `${num(s.segments)} segmentos` : 'sin datos'}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Historial de SMS</div>
            <div style={{ fontSize: 11, color: G.muted, marginTop: 3 }}>
              Todo intento de envío · horas en {getAppTz()}
              {cfg && provReady && (
                <span style={{ color: G.green, marginLeft: 8 }}>
                  ● {cfg.providerLabel || cfg.provider} · {cfg.senderId || 'sin remitente'}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { load(); loadProvider(); }}>↻ Refrescar</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setProvOpen(true)}>⚙ Proveedor</button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setColsOpen((o) => !o)}>▤ Columnas</button>
              {colsOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, background: G.card, border: `1px solid ${G.border}`, borderRadius: 10, padding: 12, minWidth: 190, boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
                  {SMS_COLS.map((c) => (
                    <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 2px', fontSize: 12, color: G.white, cursor: 'pointer' }}>
                      <input type="checkbox" checked={visibleCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                      {c.label}
                    </label>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, borderTop: `1px solid ${G.border}`, paddingTop: 10 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setVisibleCols(new Set(SMS_COLS.map((c) => c.key)))}>Todas</button>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setVisibleCols(new Set(DEFAULT_COLS))}>Por defecto</button>
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setDense((d) => !d)}>{dense ? '↕ Normal' : '↕ Compacta'}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('csv')} disabled={exporting}>↓ CSV</button>
            <button className="btn btn-ghost btn-sm" onClick={() => exportAll('xlsx')} disabled={exporting}>↓ Excel</button>
          </div>
        </div>

        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="filters" style={{ marginBottom: 12 }}>
            <input
              className="search"
              placeholder="Buscar por teléfono o texto del mensaje…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
            />
            <input
              className="form-input"
              type="date"
              style={{ width: 'auto' }}
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPage(1); }}
              title="Desde"
            />
            <input
              className="form-input"
              type="date"
              style={{ width: 'auto' }}
              value={to}
              onChange={(e) => { setTo(e.target.value); setPage(1); }}
              title="Hasta"
            />
            <button className="btn btn-gold btn-sm" onClick={applySearch}>Buscar</button>
            {hasFilters && <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Resetear</button>}
          </div>

          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'center', paddingBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 10.5, color: G.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>Estado</span>
              <div className="pill-group">
                {STATUS_PILLS.map((p) => {
                  const c = pillCount(p.v);
                  return (
                    <button
                      key={p.v || 'all'}
                      className={`pill${status === p.v ? ' active' : ''}`}
                      onClick={() => pick(setStatus)(p.v)}
                    >
                      {p.l}
                      {c != null && <span style={{ marginLeft: 6, opacity: 0.65, fontFamily: 'DM Mono, monospace' }}>{c}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 10.5, color: G.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>Origen</span>
              <div className="pill-group">
                {ORIGIN_PILLS.map((p) => (
                  <button
                    key={p.v || 'all'}
                    className={`pill${origin === p.v ? ' active' : ''}`}
                    onClick={() => pick(setOrigin)(p.v)}
                  >
                    {p.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key, c.sortable)}
                    style={{ cursor: c.sortable ? 'pointer' : 'default', whiteSpace: 'nowrap', textAlign: c.num ? 'right' : 'left', padding: pad }}
                    title={c.sortable ? 'Ordenar' : undefined}
                  >
                    {c.label}
                    {sort === c.key && <span style={{ color: G.gold, marginLeft: 5 }}>{order === 'asc' ? '▲' : '▼'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={cols.length} style={{ textAlign: 'center', padding: 36, color: G.muted }}>Cargando…</td></tr>
              )}

              {!loading && failed && (
                <tr><td colSpan={cols.length} style={{ padding: 36 }}>
                  <div className="empty">
                    <div className="empty-icon">⚠</div>
                    <div style={{ fontSize: 14, color: G.dim, marginBottom: 4 }}>No se pudo cargar el historial</div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 12 }}>La API no respondió. Vuelve a intentarlo.</div>
                    <button className="btn btn-ghost btn-sm" onClick={load}>Reintentar</button>
                  </div>
                </td></tr>
              )}

              {!loading && !failed && rows.length === 0 && (
                <tr><td colSpan={cols.length} style={{ padding: 36 }}>
                  <div className="empty">
                    <div className="empty-icon">✉</div>
                    <div style={{ fontSize: 14, color: G.dim, marginBottom: 4 }}>
                      {hasFilters ? 'Ningún envío con estos filtros' : 'Todavía no se ha intentado ningún SMS'}
                    </div>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 12 }}>
                      {hasFilters
                        ? 'Prueba a quitar filtros o a ampliar el rango de fechas.'
                        : 'Cada envío (de campaña, automático o manual) aparecerá aquí, incluso si falla.'}
                    </div>
                    {hasFilters
                      ? <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Quitar filtros</button>
                      : !provReady && <button className="btn btn-gold btn-sm" onClick={() => setProvOpen(true)}>Configurar proveedor</button>}
                  </div>
                </td></tr>
              )}

              {!loading && !failed && rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => openDetail(r)}
                  style={{ cursor: 'pointer' }}
                  title="Ver detalle"
                >
                  {cols.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: pad,
                        whiteSpace: 'nowrap',
                        textAlign: c.num ? 'right' : 'left',
                        fontFamily: c.num || c.mono ? 'DM Mono, monospace' : undefined,
                        color: c.key === 'to' ? G.white : undefined,
                      }}
                    >
                      {renderCell(r, c.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 22px', borderTop: `1px solid ${G.border}`, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace' }}>
            {total === 0
              ? 'Sin registros'
              : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} de ${num(total)}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              className="form-input"
              style={{ width: 'auto', padding: '5px 28px 5px 10px', fontSize: 11 }}
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            >
              {[10, 20, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: 11, color: G.muted }}>por página</span>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            <span style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', minWidth: 62, textAlign: 'center' }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        </div>
      </div>

      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
      {provOpen && (
        <ProviderModal
          data={provider}
          toast={toast}
          onClose={() => setProvOpen(false)}
          onSaved={() => { loadProvider(); load(); }}
        />
      )}
    </div>
  );
}
